import React, { useMemo, useState } from "react";
import { CloudRain, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "../../../utils/cn";
import { AGENDA_FEATURE_FLAGS } from "../constants/agendaFeatureFlags";
import type { AgendaWeatherAlert } from "../agendaWeatherTypes";

type WeatherAlertProps = {
  alerts: AgendaWeatherAlert[];
  onViewAffected: (alert: AgendaWeatherAlert) => void;
};

const WeatherAlert: React.FC<WeatherAlertProps> = ({
  alerts,
  onViewAffected,
}) => {
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(
    () =>
      [...alerts].sort((a, b) => {
        const severityRank = { severe: 0, high: 1, medium: 2, low: 3 };
        const diff =
          (severityRank[a.severity] ?? 9) - (severityRank[b.severity] ?? 9);
        if (diff !== 0) {
          return diff;
        }
        return a.startTime.localeCompare(b.startTime);
      }),
    [alerts]
  );

  if (sorted.length === 0) {
    return null;
  }

  const primary = sorted[0];
  const extraCount = sorted.length - 1;
  const visibleAlerts = expanded ? sorted : [primary];

  return (
    <div className="mb-5 space-y-2">
      {visibleAlerts.map((alert) => (
        <article
          key={`${alert.cityName}-${alert.startTime}-${alert.endTime}`}
          className="flex flex-col gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 md:flex-row md:items-center md:justify-between"
        >
          <div className="flex min-w-0 gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
              <CloudRain className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-sky-950">{alert.title}</p>
              <p className="mt-0.5 text-sm text-sky-900/80">
                {alert.affectedVisits} visitas presenciais nesse período, com{" "}
                {alert.affectedClients} clientes.
              </p>
              <p className="mt-0.5 text-xs text-sky-900/70">
                Confirme com os clientes ou ofereça videochamada.
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onViewAffected(alert)}
              className="rounded-lg border border-sky-300 bg-white px-3 py-2 text-sm font-semibold text-sky-900 hover:bg-sky-100"
            >
              Ver visitas afetadas
            </button>
            {AGENDA_FEATURE_FLAGS.bulkWhatsAppRainAlert ? (
              <button
                type="button"
                disabled
                title="Disponível em breve"
                className="rounded-lg border border-sky-200 px-3 py-2 text-sm font-semibold text-sky-800/60"
              >
                Avisar clientes no WhatsApp
              </button>
            ) : null}
          </div>
        </article>
      ))}

      {extraCount > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className={cn(
            "inline-flex items-center gap-1 text-sm font-semibold text-sky-800 hover:text-sky-950"
          )}
        >
          {expanded ? (
            <>
              Ocultar alertas <ChevronUp className="h-4 w-4" aria-hidden />
            </>
          ) : (
            <>
              +{extraCount} {extraCount === 1 ? "cidade" : "cidades"} com risco{" "}
              <ChevronDown className="h-4 w-4" aria-hidden />
            </>
          )}
        </button>
      ) : null}
    </div>
  );
};

export default WeatherAlert;
